// tslint:disable: no-string-literal
import { Injectable, Injector, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { ViewState, CatalogList } from './store/+state/stores.reducer';
import {
  ViewStateUpdate,
  SelectCategory,
  ViewOrderItem,
  ErrorMessage,
  ViewOrderStatus,
  ValidateStoreLocations,
  SetPendingSharedItem,
  ClearPendingSharedItem,
} from './store/+state/stores.actions';
import { Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import {
  getCurrentError,
  getSelectedStoreCatalog,
  getCurrentCartState,
  getStoreLocationsState,
  getSelectedLang,
  getPendingSharedItem,
} from './store/+state/stores.selectors';
import { getSelectedStore } from './store/+state/stores.selectors';
import { PendingSharedItemReference } from './store/+state/stores.reducer';
import { StoreService } from './store/store.service';
import {
  ClientStore,
  LocationType,
  Order,
  StoreViewState,
} from '../stores/stores';
import { combineLatest, Subject } from 'rxjs';
import { WindowRefService } from '../window.service';
import { CompleteTrustPaymentsPayment } from './payments/+state/payment.actions';
import { TrustPaymentsPaymentInfo } from './payments/payment.types';
import { environment } from '../../environments/environment';
import { APPMODE } from '../../environments/appmode.enum';
import { WhitelabelService } from '../shared/whitelabel.service';
import { takeUntil } from 'rxjs/operators';
import { CookieService } from 'ngx-cookie-service';
import {
  buildUrlWithoutLocationLabel,
  hasLocationInHash,
} from './store/utils/LocationUrlUtils';

@Injectable({
  providedIn: 'root',
})
export class LocationService implements OnDestroy {
  private destroy$ = new Subject<void>();
  selectedStore: ClientStore;
  selectedLang: string;
  catalog: CatalogList;
  cart: Order;
  refreshAction: string = null;
  refreshActionAfterLocationValidation: string = null;
  static orderUuid: string = null;
  static bookingId: number;
  locale: string = null;
  storeLocation: string = null;
  basketEnabled: boolean = null;
  private storeLocationUrl: string = null;
  trustPaymentsTransactionReference: string = null;
  private locationTypeSelected: LocationType;
  private currentView: string = '';

  // Cached cookie values to prevent unnecessary re-renders
  private cachedLocationLabel: string | null = null;
  private cachedLocationType: 'url-location' | 'cart-location' | null = null;
  private cachedLocationOrder: string | null = null;
  private lastStoreAliasName: string | null = null;
  private isCacheValid: boolean = false;

  // Printer name cache (stored from ?printer=... in customer URL)
  private cachedPrinterName: string | null = null;
  private cachedPrinterStoreId: number | null = null;
  private isPrinterCacheValid: boolean = false;

  private lastLoadViewPath: string | null = null;
  private lastLoadViewTime: number = 0;

  private hydrationEchoAnchor: string | null = null;
  private hydrationEchoTime: number = 0;
  private static readonly HYDRATION_ECHO_WINDOW_MS = 2000;

  // Terminal / confirmation views reached once at the end of an order flow.
  // The loadView same-path dedupe must never suppress a render for these
  // (see loadView + card #10584): store-loading legitimately re-issues
  // loadView for the same path as the store finishes loading, and dropping
  // that repeat strands the render (e.g. the Thank You page never appears).
  static readonly TERMINAL_VIEW_KEYWORDS = [
    'thankyou',
    'paymentProgress',
    'paymentPay',
    'qrCodePay',
    'standalone-payment',
  ];

  // Matches a `#l/<location>` segment in the URL hash. Shared between the
  // invalid-location test and the strip so they can't drift.
  static readonly LOCATION_HASH_SEGMENT = /#l\/[^\/]+\/?/;

  currentLoadView: string = '';

  // Story #10478 - guards against firing the resolve+redirect twice for the same
  // pending reference on the same branch.
  private resolvingSharedItemKey: string | null = null;
  private hydratedSharedItemKey: string | null = null;
  private lazyStoreService: StoreService | null = null;

  constructor(
    private store: Store<ViewState>,
    private router: Router,
    private route: ActivatedRoute,
    private windowRefSer: WindowRefService,
    private location: Location,
    private whitelabelService: WhitelabelService,
    private cookieService: CookieService,
    private injector: Injector,
  ) {
    combineLatest([this.store.select(getSelectedStore), this.route.queryParams])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([selectedStore, queryParams]) => {
        if (selectedStore?.id) {
          this.setBasketEnabled(queryParams.basket === 'false' ? false : null);
          // Update cache when store changes
          if (selectedStore.aliasName !== this.lastStoreAliasName) {
            this.invalidateCache();
            this.lastStoreAliasName = selectedStore.aliasName;
            this.updateCookieCachedValues();
          }
          // Story #10478 - hydrate a pending shared item reference carried into a
          // branch via ?sharedParentStore=&sharedParentOffer= (cross-subdomain
          // navigation from the parent selector, where sessionStorage does not
          // carry over). Only when the current store is not the parent itself.
          this.hydratePendingSharedItemFromQueryParams(
            selectedStore,
            queryParams,
          );
        }
      });

    combineLatest([
      this.store.select(getSelectedStore),
      this.store.select(getSelectedStoreCatalog),
      this.store.select(getCurrentCartState),
      this.store.select(getStoreLocationsState),
      this.store.select(getSelectedLang),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        ([selectedStore, catalog, cartState, storeLocation, selectedLang]) => {
          if (!storeLocation || !storeLocation?.isValid) {
            this.storeLocation = null;
          }

          if (selectedStore && catalog.data.catalogId !== -1) {
            this.selectedStore = selectedStore;
            if (!this.storeLocationUrl && this.getLocationLabel()) {
              this.storeLocationUrl = this.getLocationLabel();

              if (
                this.selectedStore?.id &&
                this.getLocationType() == 'url-location'
              ) {
                this.store.dispatch(
                  new ValidateStoreLocations(
                    this.selectedStore.id,
                    this.getLocationLabel(),
                  ),
                );
              }
            }
            this.catalog = catalog;
            if (cartState) {
              this.cart = cartState;
            }
            if (
              storeLocation &&
              storeLocation.isValid &&
              storeLocation.id !== -1
            ) {
              this.storeLocation = storeLocation.label;
              if (this.refreshActionAfterLocationValidation) {
                this.loadView(this.refreshActionAfterLocationValidation);
                // 04.06.2020: don't persist the location to the current order
                // https://trello.com/c/dOfSiejV/1640-order-with-location-improvments
                this.refreshActionAfterLocationValidation = null;
              }
            }
            if (this.refreshAction) {
              // Consume before calling: loadView may legitimately re-defer
              // (set refreshAction again) when the store is still hydrating,
              // and nulling afterwards would wipe that out. Also reset the
              // same-path dedupe (#10405): a deferred render replays the exact
              // path loadView just recorded, and when the catalog loads within
              // 100ms the dedupe would swallow the replay, stranding the view
              // on the loader (intermittent parent deep-link stall, #10478).
              const deferredPath = this.refreshAction;
              this.refreshAction = null;
              this.lastLoadViewPath = null;
              this.loadView(deferredPath);
            }
          }
          if (selectedLang) {
            this.selectedLang = selectedLang;
          }
        },
      );
    // Story #10478 - once the chosen branch (a store other than the parent that
    // held the shared reference) has its catalog loaded, resolve the parent's
    // shared offer to the branch's own offer and redirect so the item page opens
    // first. Silent fallback (no toast) when the branch does not carry the item.
    combineLatest([
      this.store.select(getSelectedStore),
      this.store.select(getSelectedStoreCatalog),
      this.store.select(getPendingSharedItem),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([selectedStore, catalog, pending]) => {
        this.maybeResolvePendingSharedItem(selectedStore, catalog, pending);
      });

    this.store
      .select(getCurrentError)
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => {
        if (state && state.status !== 'INITIAL') {
          this.store.dispatch(
            new ViewStateUpdate(
              {
                state: 'ERRORPAGE',
              },
              {
                src: 'LocationService',
                description: 'Error has occured',
              },
            ),
          );
        }
      });

    this.store
      .select(getStoreLocationsState)
      .pipe(takeUntil(this.destroy$))
      .subscribe((storeLocation) => {
        if (storeLocation && !storeLocation.isValid) {
          const currentUrl = window.location.href;
          // Only react to an invalid location when the URL actually carries a
          // `#l/<location>` segment. Location-less / terminal views (e.g.
          // `#thankyou`, `#paymentProgress`) legitimately have no location, so
          // clearing the pending view render (`refreshAction`) here would
          // strand that render and leave the Thank You page stuck on the
          // loader (card #10584).
          if (LocationService.LOCATION_HASH_SEGMENT.test(currentUrl)) {
            this.refreshAction = null;
            this.refreshActionAfterLocationValidation = null;
            const newUrl = currentUrl.replace(
              LocationService.LOCATION_HASH_SEGMENT,
              '#',
            );
            history.pushState({}, '', newUrl);
          }
        }
      });
  }
  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Story #10478 - reads the shared item reference carried in the branch URL query
   * params and, when present and this store is the branch (not the parent), stores
   * it as the pending reference so the resolve+redirect subscription can fire.
   */
  private hydratePendingSharedItemFromQueryParams(
    selectedStore: ClientStore,
    queryParams: { [key: string]: any },
  ): void {
    const parentStoreId = parseInt(queryParams?.sharedParentStore, 10);
    const parentOfferId = parseInt(queryParams?.sharedParentOffer, 10);
    if (
      !Number.isFinite(parentStoreId) ||
      !Number.isFinite(parentOfferId) ||
      parentStoreId <= 0 ||
      parentOfferId <= 0
    ) {
      return;
    }
    // Guard against re-dispatching for the same reference on this store.
    const hydrationKey = `${selectedStore.id}:${parentStoreId}:${parentOfferId}`;
    if (this.hydratedSharedItemKey === hydrationKey) {
      return;
    }
    this.hydratedSharedItemKey = hydrationKey;
    this.store.dispatch(new SetPendingSharedItem(parentStoreId, parentOfferId));
    // Strip the one-time params from the URL so a later refresh or reshare of
    // the branch URL does not re-trigger the resolve.
    const urlTree = this.router.parseUrl(this.router.url);
    delete urlTree.queryParams['sharedParentStore'];
    delete urlTree.queryParams['sharedParentOffer'];
    // Card #11081 - set the offer fragment in the same replaceState, and hold the
    // view on LOADING until the resolve answers.
    //
    // The fragment is NOT what suppresses the catalog render at this point:
    // LOADED is already the live view state by the time this runs (the boot
    // Scroll fires loadView(null) -> default case long before LoadStoreSuccess
    // populates selectedStore.id and lets this method run). What it does is stop
    // the NEXT default-case loadView (e.g. a stray Scroll / loadProperView) from
    // dispatching LOADED over the item, and keep the item page across a refresh.
    //
    // The LOADING dispatch is what removes the catalog flash: it replaces the
    // catalog render with the loader until maybeResolvePendingSharedItem knows
    // which offer id this branch actually stocks. We deliberately do NOT render
    // `offer/${parentOfferId}` here - that id is only valid when the branch
    // inherits the parent catalog, and rendering it blind would fire the offer
    // endpoint with a foreign id on copied catalogs (404 -> /not-found via
    // ErrorInterceptor), breaking story #10478's silent fallback.
    const offerPath = `offer/${parentOfferId}`;
    urlTree.fragment = offerPath;
    // #11242 - use navigateByUrl so router.url is updated together with the
    // browser address bar (replaceState bypasses Angular Router's state).
    this.hydrationEchoAnchor = offerPath;
    this.hydrationEchoTime = Date.now();
    this.router.navigateByUrl(urlTree, { replaceUrl: true });
    this.store.dispatch(
      new ViewStateUpdate(
        { state: 'LOADING' },
        {
          src: 'LocationService',
          description: 'Shared item pending resolve, hold the catalog render',
        },
      ),
    );
  }

  /**
   * Card #11081 - drops a `#offer/{id}` fragment left behind by the shared-item
   * hydration. Without this the default-case guard in `loadView` keeps
   * suppressing the only LOADED dispatch reachable on a branch, so the catalog
   * home can never come back once the resolve declines.
   */
  private clearOfferFragment(): void {
    if (this.windowRefSer.nativeWindowLocation.hash.includes('#offer')) {
      this.windowRefSer.nativeWindowLocation.hash = '';
    }
  }

  /**
   * Card #11081 - puts the branch back on the catalog home after the shared-item
   * resolve declines or fails, undoing the LOADING state the hydration set.
   * Story #10478's contract is a silent fallback to the branch catalog, so this
   * must never surface an error state.
   */
  private restoreCatalogView(description: string): void {
    this.store.dispatch(
      new ViewStateUpdate(
        { state: 'LOADED' },
        {
          src: 'LocationService',
          description,
        },
      ),
    );
  }

  /**
   * Story #10478 - true when the store is a multi-store parent whose home page is a
   * selector (child list or delivery/pickup branch picker), i.e. it must never show
   * item detail. A CURRENT-mode parent (or realtime-confirmation marketplace) shows
   * its own catalog and is treated like a normal store.
   */
  private isSelectorModeParent(store: ClientStore): boolean {
    if (store?.settings?.REQUIRES_REALTIME_CONFIRMATION) {
      return false;
    }
    // A branch (a store that itself has a parent) must never be treated as the
    // selector-mode parent, even if it inherits PARENT_STORE_HOME_PAGE in its
    // settings — otherwise the branch would swallow its own offer deep-link.
    if (store?.relation?.parentStore) {
      return false;
    }
    const homePage = store?.settings?.PARENT_STORE_HOME_PAGE;
    if (homePage) {
      // Trust the explicit setting: it arrives with the initial store payload,
      // while relation.childStores may be populated on a later ngrx emission.
      // Requiring childStores here would race on first load and wrongly send a
      // parent deep-link down the item-detail path.
      return homePage !== 'CURRENT';
    }
    // No explicit setting: infer from child-store presence
    // (defaults to CHILD_LIST_SELECTION when children exist).
    return (store?.relation?.childStores?.length ?? 0) > 0;
  }

  /**
   * Story #10478 - lazily resolves StoreService via the Injector. StoreService
   * itself depends on LocationService, so a constructor injection here would form a
   * DI cycle; a lazy `injector.get` at call time breaks it.
   */
  private getStoreService(): StoreService {
    if (!this.lazyStoreService) {
      this.lazyStoreService = this.injector.get(StoreService);
    }
    return this.lazyStoreService;
  }

  /**
   * Story #10478 - resolve the parent's shared offer to the chosen branch's own
   * offer and redirect to `#offer/{branchOfferId}` so the item page opens first.
   * Only runs once per (branch, reference); silent fallback when not carried.
   */
  private maybeResolvePendingSharedItem(
    selectedStore: ClientStore,
    catalog: CatalogList,
    pending: PendingSharedItemReference | null,
  ): void {
    if (
      !pending ||
      !selectedStore?.id ||
      !catalog ||
      catalog.data.catalogId === -1
    ) {
      return;
    }
    // Still on the parent that holds the reference - the selector is showing; wait
    // until the customer is routed into a branch (a different store).
    if (selectedStore.id === pending.parentStoreId) {
      return;
    }
    const resolveKey = `${selectedStore.id}:${pending.parentStoreId}:${pending.parentOfferId}`;
    if (this.resolvingSharedItemKey === resolveKey) {
      return;
    }
    this.resolvingSharedItemKey = resolveKey;

    // Card #11081 - when the branch inherits the parent catalog the parent's
    // offer id IS a valid id here, so the item can open immediately instead of
    // waiting out the resolve round-trip. Gated on the offer actually being in the
    // loaded catalog, so a copied/independent catalog never fires the offer
    // endpoint with a foreign id.
    const alreadyStocked = (catalog.data?.offers ?? []).some(
      (offer) => offer.offerId === pending.parentOfferId,
    );
    let renderedOfferId: number | null = null;
    if (alreadyStocked) {
      renderedOfferId = pending.parentOfferId;
      this.loadView(`offer/${pending.parentOfferId}`);
    }

    this.getStoreService()
      .resolveParentOffer(
        selectedStore.id,
        pending.parentOfferId,
        pending.parentStoreId,
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          // Drop the reference either way - it has now been consumed for this branch.
          this.store.dispatch(new ClearPendingSharedItem());
          if (response?.carried && response.offerId) {
            // Card #11081 - the eager render above may already be showing this
            // exact offer (inherited catalog, where resolve returns the same id).
            // Re-rendering it would blank the open item page and rebuild it once
            // the offer GET returns, discarding any option/variant selections
            // made meanwhile, and would duplicate that GET and the view-item
            // tracking event.
            if (renderedOfferId === response.offerId) {
              return;
            }
            // Redirect into the branch item so the existing #offer flow opens the
            // item page first (before catalog home / basket). No toast on fallback.
            // The hash must be set too: loadView's default case dispatches LOADED
            // whenever the hash carries no '#offer', so a later default-case call
            // (e.g. a stray Scroll/loadProperView) would hide the item a moment
            // after it opened. With the hash in place that guard protects us, and
            // a refresh keeps the item page.
            this.windowRefSer.nativeWindowLocation.hash = `offer/${response.offerId}`;
            this.loadView(`offer/${response.offerId}`);
            return;
          }
          // Not carried by this branch. Card #11081 - the hydration left a
          // `#offer/{parentOfferId}` fragment behind; drop it and render the
          // catalog home, otherwise the default-case '#offer' guard suppresses
          // every later LOADED dispatch and the customer is stranded on the
          // loader with no way back to the menu. Skipped when the eager render
          // already opened a genuinely stocked item - that page stays.
          if (renderedOfferId === null) {
            this.clearOfferFragment();
            this.restoreCatalogView('Shared item not carried by this branch');
          }
        },
        error: () => {
          // Silent fallback (story #10478): drop the reference and let the customer
          // land on the branch catalog home. Never surface public.global.errorExpected.
          this.store.dispatch(new ClearPendingSharedItem());
          if (renderedOfferId === null) {
            this.clearOfferFragment();
            this.restoreCatalogView('Shared item resolve failed');
          }
        },
      });
  }

  /**
   * Invalidates the cookie cache, forcing a refresh on next access
   * @private
   */
  private invalidateCache(): void {
    this.isCacheValid = false;
    this.cachedLocationLabel = null;
    this.cachedLocationType = null;
    this.cachedLocationOrder = null;

    this.isPrinterCacheValid = false;
    this.cachedPrinterName = null;
    this.cachedPrinterStoreId = null;
  }

  setPrinterName(storeId: number, printerName: string): void {
    if (!storeId || !printerName) {
      return;
    }
    // Persist for a week (same as table/room style persistence)
    this.cookieService.set(`printer-name-${storeId}`, printerName, 7);
    this.invalidateCache();
  }

  getPrinterName(storeId: number): string | null {
    if (!storeId) {
      return null;
    }

    if (this.isPrinterCacheValid && this.cachedPrinterStoreId === storeId) {
      return this.cachedPrinterName;
    }

    const value = this.cookieService.get(`printer-name-${storeId}`);
    this.cachedPrinterName = value || null;
    this.cachedPrinterStoreId = storeId;
    this.isPrinterCacheValid = true;
    return this.cachedPrinterName;
  }

  removePrinterFromUrl(): void {
    try {
      const url = new URL(this.windowRefSer.nativeWindowLocation.href);
      if (!url.searchParams.has('printer')) {
        return;
      }
      url.searchParams.delete('printer');
      const newPath = `${url.pathname}${url.search}${url.hash}`;
      this.location.replaceState(newPath);
    } catch {
      // no-op: do not break customer flow if URL parsing fails
    }
  }

  /**
   * Updates cached cookie values to prevent repeated cookie reads
   * Handles null/undefined cases properly to avoid unnecessary re-renders
   * @private
   */
  private updateCookieCachedValues(): void {
    if (!this.selectedStore?.aliasName) {
      this.invalidateCache();
      return;
    }

    const storeAlias = this.selectedStore.aliasName;

    // Check URL first for location label
    const urlLocationMatch = this.router.url.match(/\/#l\/([^/?#]+)/);
    const urlLocation = urlLocationMatch ? urlLocationMatch[1] : null;

    // Cache location label (URL takes precedence)
    if (urlLocation) {
      this.cachedLocationLabel = urlLocation;
    } else {
      const cookieLabel = this.cookieService.get(
        `location-order-label-${storeAlias}`,
      );
      this.cachedLocationLabel = cookieLabel || null;
    }

    // Cache location type
    const cookieType = this.cookieService.get(
      `location-order-type-${storeAlias}`,
    );
    this.cachedLocationType =
      (cookieType as 'url-location' | 'cart-location') || null;

    // Cache location order
    const cookieOrder = this.cookieService.get(`location-order-${storeAlias}`);
    this.cachedLocationOrder = cookieOrder || null;

    this.isCacheValid = true;
  }

  /**
   * Ensures cache is valid before reading cached values
   * @private
   */
  private ensureCacheValid(): void {
    if (
      !this.isCacheValid ||
      this.lastStoreAliasName !== this.selectedStore?.aliasName
    ) {
      this.updateCookieCachedValues();
    }
  }

  /**
   * Bottom menu bar pages belong to the template apps (story #10267). Their
   * routes only exist in a Template build, and this guard covers the other way
   * in — a `#widget-…` fragment typed on a customer UI, kiosk or admin order
   * capture URL — so those apps can never enter a widget view.
   */
  private dispatchWidgetView(
    state: StoreViewState['state'],
    description: string,
  ): void {
    if (environment.mode !== APPMODE.Template) {
      return;
    }
    this.store.dispatch(
      new ViewStateUpdate({ state }, { src: 'LocationService', description }),
    );
  }

  loadView(path) {
    if (
      path &&
      path === this.hydrationEchoAnchor &&
      Date.now() - this.hydrationEchoTime <
        LocationService.HYDRATION_ECHO_WINDOW_MS
    ) {
      this.hydrationEchoAnchor = null;
      return;
    }

    // The 100ms same-path dedupe (#10405) must not suppress a render for a
    // terminal/confirmation view — those are reached once after payment and
    // store-loading may legitimately re-issue loadView for the same path as
    // the store finishes loading. Dropping that repeat strands the render and
    // leaves the Thank You page stuck on the loader (card #10584).
    const isTerminalView =
      typeof path === 'string' &&
      LocationService.TERMINAL_VIEW_KEYWORDS.some((keyword) =>
        path.includes(keyword),
      );
    const now = Date.now();
    if (
      !isTerminalView &&
      path === this.lastLoadViewPath &&
      now - this.lastLoadViewTime < 100
    ) {
      return;
    }
    this.lastLoadViewPath = path;
    this.lastLoadViewTime = now;

    let urlParams = [''];
    if (path) {
      urlParams = path.split('?')[0].split('/');
      while (urlParams.length) {
        if (urlParams[0] === 'orderUuid') {
          LocationService.orderUuid = urlParams[1];
        } else if (urlParams[0] === 'l') {
          this.storeLocationUrl = urlParams[1];
          if (this.selectedStore) {
            this.setLocationUrlType(
              this.selectedStore.aliasName,
              'url-location',
            );
            if (this.storeLocation) {
              if (this.storeLocation !== urlParams[1]) {
                if (this.selectedStore) {
                  this.refreshActionAfterLocationValidation = path;
                  this.store.dispatch(
                    new ValidateStoreLocations(
                      this.selectedStore.id,
                      urlParams[1],
                    ),
                  );
                }
                this.refreshAction = path;
              }
            } else {
              if (this.selectedStore) {
                this.refreshActionAfterLocationValidation = path;
                this.store.dispatch(
                  new ValidateStoreLocations(
                    this.selectedStore.id,
                    urlParams[1],
                  ),
                );
              }
              this.refreshAction = path;
            }
          }
        } else {
          break;
        }
        urlParams.splice(0, 2);
      }
    }

    if (
      !this.storeLocationUrl &&
      this.getLocationLabel() &&
      this.getLocationType() === 'url-location'
    ) {
      this.storeLocationUrl = this.getLocationLabel();
      if (this.selectedStore?.id) {
        this.store.dispatch(
          new ValidateStoreLocations(
            this.selectedStore.id,
            this.getLocationLabel(),
          ),
        );
      }
    }

    this.currentView = urlParams[0];

    switch (urlParams[0]) {
      case 'category':
        if (this.selectedStore && this.catalog) {
          if (!this.selectedStore) {
            this.store.dispatch(
              new ErrorMessage('public.global.errorExpected'),
            );
            return;
          }
          this.store.dispatch(
            new SelectCategory(parseInt(urlParams[1], 10), {
              src: 'LocationService',
              description: 'Load view, category',
            }),
          );
          this.store.dispatch(
            new ViewStateUpdate(
              { state: 'LOADED' },
              {
                src: 'LocationService',
                description: 'Load view, category',
              },
            ),
          );
        } else {
          this.refreshAction = path;
        }
        break;
      case 'standalone-payment':
        this.store.dispatch(
          new ViewStateUpdate(
            { state: 'STANDALONEPAYMENT' },
            {
              src: 'LocationService',
              description: 'Load view, standalone payment',
            },
          ),
        );
        break;
      case 'offer': {
        const offerId = parseInt(urlParams[1], 10);
        if (this.selectedStore && this.catalog) {
          // Story #10478 - defer when the store's settings haven't arrived yet.
          // isSelectorModeParent reads PARENT_STORE_HOME_PAGE from settings; without
          // it we'd wrongly take the offer path on the parent and flash item detail.
          // refreshAction is re-consumed on the next combineLatest emission.
          if (!this.selectedStore?.settings) {
            this.refreshAction = path;
            break;
          }
          if (offerId) {
            // Story #10478 - a selector-mode parent must never render item detail.
            // Hold the parent's shared offer reference and let the selector show;
            // the resolve+redirect (constructor subscription) opens the branch item
            // once a branch is chosen. A CURRENT-mode parent or a normal store keeps
            // today's behaviour and opens the item directly.
            if (this.isSelectorModeParent(this.selectedStore)) {
              this.store.dispatch(
                new SetPendingSharedItem(this.selectedStore.id, offerId),
              );
              // Let the store render (shows the multi-store selector); the
              // pending item reference will be carried through once a branch is picked.
              this.store.dispatch(
                new ViewStateUpdate(
                  { state: 'LOADED' },
                  {
                    src: 'LocationService',
                    description:
                      'Load view, selector-mode parent offer deep-link',
                  },
                ),
              );
              break;
            }
            this.store.dispatch(
              new ViewOrderItem(
                this.selectedStore.id,
                this.catalog.data.catalogId,
                offerId,
                '',
                this.selectedLang,
              ),
            );
            this.store.dispatch(
              new ViewStateUpdate(
                { state: 'VIEWPRODUCTDETAILS' },
                {
                  src: 'LocationService',
                  description: 'Load view, offer',
                },
              ),
            );
          } else {
            this.store.dispatch(
              new ErrorMessage('public.global.errorExpected'),
            );
          }
        } else {
          this.refreshAction = path;
        }
        break;
      }
      case 'orderItem': {
        const orderId = urlParams[1];
        if (
          this.selectedStore &&
          this.catalog &&
          this.cart &&
          this.cart.orderItems
        ) {
          if (orderId) {
            let offerId = null;
            this.cart.orderItems.forEach((orderItem) => {
              if (orderItem.uuid === orderId) {
                offerId = orderItem.offerId;
              }
            });
            if (offerId) {
              this.store.dispatch(
                new ViewOrderItem(
                  this.selectedStore.id,
                  this.catalog.data.catalogId,
                  offerId,
                  orderId,
                  this.selectedLang,
                ),
              );
              this.store.dispatch(
                new ViewStateUpdate(
                  { state: 'VIEWPRODUCTDETAILSFROMCARTVIEW' },
                  {
                    src: 'LocationService',
                    description: 'Load view, order item',
                  },
                ),
              );
            } else {
              this.store.dispatch(
                new ErrorMessage('public.global.errorExpected'),
              );
              console.error('unexpected offerId value', offerId);
            }
          } else {
            this.store.dispatch(
              new ErrorMessage('public.global.errorExpected'),
            );
            console.error('unexpected orderUuid value', orderId);
          }
        } else {
          this.refreshAction = path;
        }
        break;
      }
      case 'cart':
        this.store.dispatch(
          new ViewStateUpdate(
            { state: 'VIEWCART' },
            {
              src: 'LocationService',
              description: 'Load view, cart',
            },
          ),
        );
        break;
      case 'checkout-recommendations':
        this.store.dispatch(
          new ViewStateUpdate(
            { state: 'CHECKOUT_RECOMMENDATIONS' },
            {
              src: 'LocationService',
              description: 'Load view, checkout recommendations',
            },
          ),
        );
        break;
      case 'login':
        this.store.dispatch(
          new ViewStateUpdate(
            { state: 'LOGIN' },
            {
              src: 'LocationService',
              description: 'Load view, login',
            },
          ),
        );
        break;
      case 'precheckout':
        this.store.dispatch(
          new ViewStateUpdate(
            { state: 'PRECHECKOUT' },
            {
              src: 'LocationService',
              description: 'Load view, precheckout',
            },
          ),
        );
        break;
      case 'profile':
        this.store.dispatch(
          new ViewStateUpdate(
            { state: 'PROFILE' },
            {
              src: 'LocationService',
              description: 'Load view, profile',
            },
          ),
        );
        break;
      case 'edit-profile':
        this.store.dispatch(
          new ViewStateUpdate(
            { state: 'EDITPROFILE' },
            {
              src: 'LocationService',
              description: 'Load view, edit profile',
            },
          ),
        );
        break;
      case 'edit-address':
        this.store.dispatch(
          new ViewStateUpdate(
            { state: 'EDITADDRESS' },
            {
              src: 'LocationService',
              description: 'Load view, edit address',
            },
          ),
        );
        break;
      case 'emptycart':
        this.store.dispatch(
          new ViewStateUpdate(
            { state: 'EMPTYCARTPAGE' },
            {
              src: 'LocationService',
              description: 'Load view, empty cart page',
            },
          ),
        );
        break;
      case 'confirmation-failed':
        this.store.dispatch(
          new ViewStateUpdate(
            { state: 'CONFIRMATIONFAILEDPAGE' },
            {
              src: 'LocationService',
              description: 'Load view, confirmation failed page',
            },
          ),
        );
        break;
      case 'checkout':
        this.store.dispatch(
          new ViewStateUpdate(
            { state: 'CHECKOUT' },
            {
              src: 'LocationService',
              description: 'Load view, checkout',
            },
          ),
        );
        break;
      case 'thankyou':
        if (this.selectedStore) {
          if (urlParams[1]) {
            let orderId = urlParams[1];
            if (urlParams[1] === 'e') {
              LocationService.orderUuid = this.cart.uuid;
              orderId = urlParams[2];
            }
            this.store.dispatch(
              new ViewOrderStatus(
                this.selectedStore.id,
                orderId,
                'CHECKEXISTING',
                this.selectedLang,
              ),
            );
            this.store.dispatch(
              new ViewStateUpdate(
                { state: 'THANKYOUPAGE' },
                {
                  src: 'LocationService',
                  description: 'Load view',
                },
              ),
            );
          } else {
            this.store.dispatch(
              new ErrorMessage('public.global.errorExpected'),
            );
            console.error('unexpected orderUuid value', urlParams[1]);
          }
        } else {
          this.refreshAction = path;
        }
        break;
      case 'verifyPayment':
        const storeId = urlParams[1];
        const orderUuid = urlParams[2];
        const status = urlParams[3];
        let reqPayload = path.split('?')[1];
        reqPayload = JSON.parse(
          '{"' +
            decodeURI(reqPayload)
              .replace(/"/g, '\\"')
              .replace(/&/g, '","')
              .replace(/=/g, '":"') +
            '"}',
        );
        if (
          this.selectedStore &&
          storeId &&
          String(this.selectedStore.id) !== storeId
        ) {
          this.store.dispatch(new ErrorMessage('public.global.errorExpected'));
          return;
        }
        if (storeId && orderUuid && reqPayload) {
          const queryParams: TrustPaymentsPaymentInfo = reqPayload;
          if (
            !this.trustPaymentsTransactionReference ||
            this.trustPaymentsTransactionReference !==
              queryParams.transactionreference
          ) {
            this.trustPaymentsTransactionReference =
              queryParams.transactionreference;
            switch (status) {
              case 'error':
              case 'declined':
              case 'success':
                this.store.dispatch(
                  new CompleteTrustPaymentsPayment(
                    Number(storeId),
                    orderUuid,
                    queryParams,
                  ),
                );
                break;
            }
          }
        } else {
          this.store.dispatch(new ErrorMessage('public.global.errorExpected'));
          console.error(
            'TrustPayments payment failed for orderUuid value',
            orderUuid,
          );
          this.refreshAction = path;
        }
        break;
      case 'paymentError':
        this.store.dispatch(
          new ViewStateUpdate(
            { state: 'ERRORPAGE' },
            {
              src: 'LocationService',
              description: 'Load view, payment error',
            },
          ),
        );
        break;
      case 'paymentProgress':
        this.store.dispatch(
          new ViewStateUpdate(
            { state: 'PAYMENTPROGRESS' },
            {
              src: 'LocationService',
              description: 'Load payment progress screen',
            },
          ),
        );
        break;
      case 'paymentProgressError':
        this.store.dispatch(
          new ViewStateUpdate(
            { state: 'PAYMENTPROGRESSERROR' },
            {
              src: 'LocationService',
              description: 'Load payment progress error screen',
            },
          ),
        );
        break;
      case 'paymentPay':
        this.store.dispatch(
          new ViewStateUpdate(
            { state: 'PAYMENTPAY' },
            {
              src: 'LocationService',
              description: 'Load payment pay screen',
            },
          ),
        );
        break;
      case 'qrCodePay':
        this.store.dispatch(
          new ViewStateUpdate(
            { state: 'QRCODEPAY' },
            {
              src: 'LocationService',
              description: 'Load qr code pay screen',
            },
          ),
        );
        break;
      case 'require-location':
        this.store.dispatch(
          new ViewStateUpdate(
            { state: 'REQUIREDLOCATION' },
            {
              src: 'LocationService',
              description: 'Load required location screen',
            },
          ),
        );
        break;
      case 'sales-point-required':
        this.store.dispatch(
          new ViewStateUpdate(
            { state: 'SALESPOINTREQUIRED' },
            {
              src: 'LocationService',
              description: 'Load required Sales Point screen',
            },
          ),
        );
        break;
      case 'kiosk-video':
        this.store.dispatch(
          new ViewStateUpdate(
            { state: 'KIOSKVIDEO' },
            {
              src: 'LocationService',
              description: 'Kiosk video screen',
            },
          ),
        );
        break;
      case 'consumption-mode':
        this.store.dispatch(
          new ViewStateUpdate(
            { state: 'CONSUMPTION_MODE' },
            {
              src: 'LocationService',
              description: 'Eat-In / Takeaway',
            },
          ),
        );
        break;
      case 'promotion':
        this.store.dispatch(
          new ViewStateUpdate(
            { state: 'PROMOTION' },
            {
              src: 'LocationService',
              description: 'Promotion details screen',
            },
          ),
        );
        break;
      case 'widget-menu':
        this.dispatchWidgetView(
          'LOADED',
          'Widget menu page (shows store dashboard)',
        );
        break;
      case 'widget-stores':
        this.dispatchWidgetView('STORES', 'Widget stores page');
        break;
      case 'widget-loyalty-promotions':
        this.dispatchWidgetView(
          'LOYALTY_PROMOTIONS',
          'Widget loyalty promotions page',
        );
        break;
      case 'widget-member-code':
        this.dispatchWidgetView('MEMBER_CODE', 'Widget member code page');
        break;
      default:
        const isThankYouPage = window.location.hash.includes('#thankyou');
        const isOfferPage = window.location.hash.includes('#offer');
        const isRecommendationsPage = window.location.hash.includes(
          '#checkout-recommendations',
        );
        if (!isThankYouPage && !isOfferPage && !isRecommendationsPage) {
          this.store.dispatch(
            new ViewStateUpdate(
              { state: 'LOADED' },
              {
                src: 'LocationService',
                description: 'Load view, default',
              },
            ),
          );
        }
        break;
    }
  }

  /**
   * get orderUuid passed through url
   * it will always take presedence over the cookie or state orderUuid
   * @returns (string) orderUuid
   */
  getOrderUuid() {
    return LocationService.orderUuid;
  }
  setOrderUuid(uuid: string) {
    LocationService.orderUuid = uuid;
  }

  /**
   * get bookingId passed through url
   * it will always take presedence over the cookie or state bookingId
   * @returns (string) bookingId
   */
  getEventBookingId() {
    return LocationService.bookingId;
  }
  setEventBookingId(bookingId: number) {
    LocationService.bookingId = bookingId;
  }

  /**
   * get storeLocation passed through url
   * it will always take presedence over the cookie or state storeLocation
   * @returns (string) storeLocation
   */
  getStoreLocation() {
    return this.storeLocation !== 'null' ? this.storeLocation : null;
  }
  getUrlStoreLocation() {
    return this.getLocationLabel() !== 'null' ? this.getLocationLabel() : null;
  }
  clearStoreLocationUrl() {
    this.removeLocationLabel(this.selectedStore?.aliasName);
  }
  setStoreLocation(location) {
    this.storeLocation = location;
  }

  public_url() {
    const url: string = this.router.url;
    return url.split('#')[0];
  }

  /**
   * Full-page redirect back to the customer UI base URL — e.g. sending the
   * customer to a fresh store homepage from the real-time confirmation failure
   * page (card #9601). Goes through WindowRefService rather than touching
   * `window.location` directly so the side-effect can be mocked in tests.
   */
  redirectToPublicUrl(): void {
    this.windowRefSer.nativeWindowLocation.href = this.public_url();
  }
  /**
   * returns customer UI base navigation url
   * it determines if orderUuid and location params should be prepended to the hash
   * @param destination string
   */
  isOrderCapture(): boolean {
    return this.public_url().includes('/capture/');
  }
  base_url(destination) {
    if (this.storeLocation && this.isAdminOrderCaptureUpdate()) {
      destination = `${destination}`;
      return `${this.public_url()}#${destination}`;
    }

    if (this.isOrderCapture() && window.location.hash.includes('#thankyou')) {
      this.router.url.split('#')[0];
      return this.public_url();
    }

    if (LocationService.orderUuid) {
      destination = `orderUuid/${LocationService.orderUuid}/${destination}`;
    }

    // Note: do not reference previous table number when admin order capture.
    if (this.storeLocation && !this.isOrderCapture()) {
      destination = `${destination}`;
    }
    if (destination === '' || window.location.hash.includes('#thankyou')) {
      return this.public_url();
    }
    return `${this.public_url()}#${destination}`;
  }

  /**
   * method to go back to previous page
   * @param fallbackPath | optional, targetted fallback path in case there is no location
   * in the history to go back to, it should include the # for instance '#catalog'
   * defaults to catalog view screen
   */
  goBack(fallbackPath = '') {
    const navState: any = this.location.getState();

    // bypass safari's issues with location history
    if (
      /safari/i.test(this.windowRefSer.nativeWindow.navigator.userAgent) &&
      !/Chrome/i.test(this.windowRefSer.nativeWindow.navigator.userAgent)
    ) {
      // tslint:disable-next-line: no-string-literal
      const plateformLocation: string = (this.location as any)
        ?._locationStrategy?._platformLocation?._location?.href;
      if (
        plateformLocation ===
        `${this.windowRefSer.nativeWindowLocation.origin}${this.router.url}`
      ) {
        this.router.navigateByUrl('');
        return; // Early return after Safari workaround
      }
    }
    try {
      if (navState?.navigationId > 1) {
        this.location.back();
      } else {
        // window.history.back() is unsafe here: in a fresh tab (direct URL / deep link)
        // it returns to the browser NTP or the last external page. Always use the
        // catalog fallback when there is no prior Angular navigation. (#11012)
        this.router.navigateByUrl(this.public_url() + fallbackPath);
      }
    } catch (e) {
      this.router.navigateByUrl(this.public_url() + fallbackPath);
    }
  }

  setBasketEnabled(basketEnabled: boolean) {
    this.basketEnabled = basketEnabled;
  }

  setLocale(locale: string) {
    this.locale = locale;
  }

  isBasketEnabled(): boolean {
    return this.basketEnabled;
  }

  isAdminOrderUpdate() {
    return this.router.url.includes('/orders/');
  }
  isAdminOrderCaptureUpdate() {
    return this.router.url.includes('/capture/');
  }

  /**
   * Determines the current admin section based on URL
   * @returns 'marketing' if URL contains '/marketing/', otherwise 'settings'
   */
  getCurrentAdminSection(): 'marketing' | 'settings' {
    const currentUrl = this.windowRefSer.nativeWindowLocation.href;
    return currentUrl.includes('/marketing/') ? 'marketing' : 'settings';
  }

  getSubdomain() {
    const domain = this.windowRefSer.nativeWindowLocation.hostname;

    if (domain.indexOf(environment.envDomain) < 0) {
      return domain;
    }

    if (
      domain.indexOf('.') < 0 ||
      domain.split('.')[0] === 'example' ||
      domain.split('.')[0] === 'lvh' ||
      domain.split('.')[0] === 'www'
    ) {
      return '';
    }
    return domain.split('.')[0];
  }

  isCustomerUI(): boolean {
    const subdomain = this.getSubdomain();
    return !(
      subdomain === '' ||
      subdomain === 'admin' ||
      this.whitelabelService.isWhiteLabelDomainBySubDomain(subdomain)
    );
  }

  setLocationLabel(storeAlias: string, locationUrl: string) {
    if (!storeAlias || !locationUrl) {
      return;
    }
    let cookieExpires = 1;

    if (
      this.locationTypeSelected == LocationType.LOCATION ||
      this.locationTypeSelected == LocationType.ROOM
    ) {
      cookieExpires = 7;
    }
    this.cookieService.set(
      `location-order-label-${storeAlias}`,
      locationUrl,
      cookieExpires,
    );
    // Invalidate cache to force refresh on next read
    this.invalidateCache();
  }

  setLocationUrlType(
    storeAlias: string,
    locationUrlType: 'url-location' | 'cart-location',
  ) {
    if (!storeAlias || !locationUrlType) {
      return;
    }
    let cookieExpires = 1;

    if (
      this.locationTypeSelected == LocationType.LOCATION ||
      this.locationTypeSelected == LocationType.ROOM
    ) {
      cookieExpires = 7;
    }
    this.cookieService.set(
      `location-order-type-${storeAlias}`,
      locationUrlType,
      cookieExpires,
    );
    // Invalidate cache to force refresh on next read
    this.invalidateCache();
  }

  removeLocationLabel(storeAlias: string) {
    this.cookieService.delete(`location-order-label-${storeAlias}`);
    // Invalidate cache after deletion
    this.invalidateCache();
  }

  // Sales Point external_id is captured per-store on customer-storefront bootstrap, persisted in a
  // cookie that lives far longer than the location cookie (kiosks are provisioned once and run for
  // months), and replayed on every subsequent bootstrap so the backend can re-validate it. The
  // cookie holds the external_id rather than the numeric id so the bootstrap endpoint can resolve it
  // case-insensitively. The numeric id surfaces on the bootstrap response and flows from there into
  // the order payload.
  getSalesPointExternalId(storeAlias: string): string | null {
    if (!storeAlias) {
      return null;
    }
    return this.cookieService.get(`sales-point-${storeAlias}`) || null;
  }

  setSalesPointExternalId(storeAlias: string, externalId: string) {
    if (!storeAlias || !externalId) {
      return;
    }
    // 10-year expiry stands in for "indefinite" — ngx-cookie-service treats `undefined` as
    // a session cookie, which would defeat the purpose for a kiosk that is provisioned once.
    const tenYearsFromNow = new Date();
    tenYearsFromNow.setFullYear(tenYearsFromNow.getFullYear() + 10);
    this.cookieService.set(
      `sales-point-${storeAlias}`,
      externalId,
      tenYearsFromNow,
    );
  }

  removeSalesPointExternalId(storeAlias: string) {
    if (!storeAlias) {
      return;
    }
    this.cookieService.delete(`sales-point-${storeAlias}`);
  }

  readSalesPointFromUrl(): string | null {
    if (typeof window === 'undefined') {
      return null;
    }
    const params = new URLSearchParams(window.location.search);
    const raw = params.get('salespoint');
    if (!raw) {
      return null;
    }
    return raw.trim() || null;
  }

  stripSalesPointFromUrl(): void {
    if (typeof window === 'undefined') {
      return;
    }
    const url = new URL(window.location.href);
    if (!url.searchParams.has('salespoint')) {
      return;
    }
    url.searchParams.delete('salespoint');
    const cleanSearch = url.searchParams.toString();
    const cleanUrl =
      url.pathname + (cleanSearch ? `?${cleanSearch}` : '') + url.hash;
    window.history.replaceState({}, '', cleanUrl);
  }

  removeLocationUrlType(storeAlias: string) {
    this.cookieService.delete(`location-order-type-${storeAlias}`);
    // Invalidate cache after deletion
    this.invalidateCache();
  }

  removeLocationOrder(storeAlias: string) {
    this.cookieService.delete(`location-order-${storeAlias}`);
    // Invalidate cache after deletion
    this.invalidateCache();
  }

  setLocationOrder(
    storeAlias: string,
    orderUuid: string,
    locationLabel: string,
    locationId: number,
    locationType: LocationType,
  ) {
    if (!locationLabel || !locationId) {
      return;
    }

    this.locationTypeSelected = locationType;
    let cookieExpires = 1;

    if (
      this.locationTypeSelected == LocationType.LOCATION ||
      this.locationTypeSelected == LocationType.ROOM
    ) {
      cookieExpires = 7;
    }

    this.cookieService.set(
      `location-order-${storeAlias}`,
      `${orderUuid}_${locationLabel}_${locationId}`,
      cookieExpires,
    );
    this.setLocationLabel(storeAlias, locationLabel);
    this.setLocationUrlType(storeAlias, this.getLocationType());
    // Invalidate cache after setting new values
    this.invalidateCache();
  }

  getLocationLabel() {
    // Early return for null/undefined cases to prevent unnecessary operations
    if (!this.selectedStore?.aliasName) {
      const urlLocationMatch = this.router.url.match(/\/#l\/([^/?#]+)/);
      return urlLocationMatch ? urlLocationMatch[1] : null;
    }

    // Ensure cache is valid before returning cached value
    this.ensureCacheValid();

    // Return cached value (null-safe, prevents re-renders on empty cookies)
    return this.cachedLocationLabel;
  }

  getLocationType(aliasName?: string): 'url-location' | 'cart-location' {
    // If specific alias is provided, read directly from cookie (rare case)
    if (aliasName && aliasName !== this.selectedStore?.aliasName) {
      const value = this.cookieService.get(`location-order-type-${aliasName}`);
      return value as 'url-location' | 'cart-location';
    }

    // Early return for null/undefined cases
    if (!this.selectedStore?.aliasName) {
      return null;
    }

    // Ensure cache is valid before returning cached value
    this.ensureCacheValid();

    // Return cached value (null-safe, prevents re-renders on empty cookies)
    return this.cachedLocationType;
  }

  getLocationOrder() {
    // Early return for null/undefined cases to prevent unnecessary operations
    if (!this.selectedStore?.aliasName) {
      return null;
    }

    // Ensure cache is valid before returning cached value
    this.ensureCacheValid();

    // Return cached value (null-safe, prevents re-renders on empty cookies)
    return this.cachedLocationOrder;
  }

  getLocationOrderUuid() {
    const locationOrder = this.getLocationOrder();
    // Null-safe: Only split if locationOrder exists and has content
    return locationOrder ? locationOrder.split('_')[0] : null;
  }

  removeLocationFromUrl(locationLabel: string) {
    try {
      this.location.replaceState(
        buildUrlWithoutLocationLabel(
          this.windowRefSer.nativeWindowLocation.href,
          locationLabel,
        ),
      );
    } catch {
      // no-op: do not break customer flow if URL parsing fails
    }
  }

  hasLocationInUrl(): boolean {
    return hasLocationInHash(this.windowRefSer?.nativeWindowLocation?.hash);
  }

  getCurrentView() {
    return this.currentView;
  }

  getParamAt(index: number): string {
    const params = this.getParams();
    if (params.length <= index) {
      return '';
    }
    return params[index];
  }

  getParams(): string[] {
    const urlParts = this.router.url.split('#');
    if (urlParts.length < 2) {
      return [];
    }
    return urlParts[1].split('/');
  }
}
